import { useState } from "react";
import axios from "axios";
import { FaTimes, FaFilePdf, FaCloudUploadAlt } from "react-icons/fa";
import { Formik, Form } from "formik";
import { useSelector } from "react-redux";

const Step3Medias = ({ form, setForm, nextStep, prevStep }) => {
  const [images, setImages] = useState(form.images || []);
  const [videos, setVideos] = useState(form.videos || []);
  const [attachments, setAttachments] = useState(form.attachments || []);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const user = useSelector((state) => state.auth.user);
  const token = user?.token;

  // Handle drag & drop
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  // Handle file input
  const handleInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  // Filter and add files to pending list
  const handleFiles = (files) => {
    const validFiles = files.filter((file) =>
      ["image/", "video/", "application/pdf"].some((type) =>
        file.type.startsWith(type)
      )
    );
    setMediaFiles((prev) => [...prev, ...validFiles]);
  };

  // Remove pending file
  const deletePendingFile = (index) => {
    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Remove uploaded file
  const deleteUploadedFile = (type, index) => {
    if (type === "image") {
      const updated = images.filter((_, i) => i !== index);
      setImages(updated);
      setForm((prev) => ({ ...prev, images: updated }));
    } else if (type === "video") {
      const updated = videos.filter((_, i) => i !== index);
      setVideos(updated);
      setForm((prev) => ({ ...prev, videos: updated }));
    } else if (type === "pdf") {
      const updated = attachments.filter((_, i) => i !== index);
      setAttachments(updated);
      setForm((prev) => ({ ...prev, attachments: updated }));
    }
  };

  // Upload files to server
  const uploadFiles = async () => {
    if (mediaFiles.length === 0) return;

    setUploading(true);

    const mediaToUpload = mediaFiles.filter((f) =>
      f.type.startsWith("image/") || f.type.startsWith("video/")
    );
    const pdfsToUpload = mediaFiles.filter((f) => f.type === "application/pdf");

    const uploadToEndpoint = async (files, url, isImageOrVideo = true) => {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      return isImageOrVideo
        ? response.data.propertyImages?.map((media) => ({
            type: media.imagePath.endsWith(".mp4") ? "video" : "image",
            url: media.image_url,
            name: media.imagePath,
          }))
        : response.data.propertyAttachments?.map((att) => ({
            type: "pdf",
            url: att.attachment_url,
            name: att.attachmentPath,
          }));
    };

    try {
      if (mediaToUpload.length > 0) {
        const uploadedMedia = await uploadToEndpoint(
          mediaToUpload,
          `${baseUrl}/all/upload/files/images`,
          true
        );
        const uploadedImages = uploadedMedia.filter((m) => m.type === "image");
        const uploadedVideos = uploadedMedia.filter((m) => m.type === "video");
        setImages((prev) => {
          const updated = [...prev, ...uploadedImages];
          setForm((f) => ({ ...f, images: updated }));
          return updated;
        });
        setVideos((prev) => {
          const updated = [...prev, ...uploadedVideos];
          setForm((f) => ({ ...f, videos: updated }));
          return updated;
        });
      }

      if (pdfsToUpload.length > 0) {
        const uploadedPdfs = await uploadToEndpoint(
          pdfsToUpload,
          `${baseUrl}/all/upload/files/attachments`,
          false
        );
        setAttachments((prev) => {
          const updated = [...prev, ...uploadedPdfs];
          setForm((f) => ({ ...f, attachments: updated }));
          return updated;
        });
      }

      setMediaFiles([]);
    } catch (err) {
      alert("Upload failed!");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Formik
      initialValues={{}}
      onSubmit={() => {
        setForm((prev) => ({
          ...prev,
          images,
          videos,
          attachments,
        }));
        if (nextStep) nextStep();
      }}
    >
      {() => (
        <Form>
          <div className="container my-4">
            <div
              className="border rounded p-5 text-center mb-4 bg-light"
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              style={{ cursor: "pointer" }}
            >
              <FaCloudUploadAlt size={40} className="text-primary mb-2" />
              <p className="text-muted">Drag & Drop or click below to upload</p>
              <input
                type="file"
                accept="image/*,video/*,application/pdf"
                multiple
                className="form-control mt-2"
                onChange={handleInputChange}
              />
            </div>

            {/* Pending Preview */}
            {mediaFiles.length > 0 && (
              <div className="mb-4">
                <h6 className="mb-2">Files to Upload</h6>
                <div className="row g-3">
                  {mediaFiles.map((file, index) => (
                    <div className="col-md-3 col-sm-4 col-6 position-relative" key={index}>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                        onClick={() => deletePendingFile(index)}
                      >
                        <FaTimes />
                      </button>
                      <div
                        className="border rounded text-center bg-white shadow-sm overflow-hidden"
                        style={{ height: "160px" }}
                      >
                        {file.type.startsWith("image/") ? (
                          <img
                            src={URL.createObjectURL(file)}
                            className="w-100 h-100 object-fit-cover"
                            alt=""
                          />
                        ) : file.type.startsWith("video/") ? (
                          <video
                            src={URL.createObjectURL(file)}
                            className="w-100 h-100 object-fit-cover"
                            controls
                          />
                        ) : (
                          <div className="d-flex flex-column justify-content-center align-items-center h-100 text-danger">
                            <FaFilePdf size={40} />
                            <p className="small mt-2">PDF</p>
                          </div>
                        )}
                      </div>
                      <p className="small mt-1 text-center text-truncate">{file.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Uploaded Preview */}
            {[{ label: "Images", data: images, type: "image" },
              { label: "Videos", data: videos, type: "video" },
              { label: "Attachments", data: attachments, type: "pdf" }
            ].map((section, secIndex) => section.data.length > 0 && (
              <div className="mb-4" key={secIndex}>
                <h6 className="mb-2">Uploaded {section.label}</h6>
                <div className="row g-3">
                  {section.data.map((item, index) => (
                    <div className="col-md-3 col-sm-4 col-6 position-relative" key={index}>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                        onClick={() => deleteUploadedFile(section.type, index)}
                      >
                        <FaTimes />
                      </button>
                      <div
                        className="border rounded text-center bg-white shadow-sm overflow-hidden"
                        style={{ height: "160px" }}
                      >
                        {section.type === "image" ? (
                          <img src={item.url} className="w-100 h-100 object-fit-cover" alt="" />
                        ) : section.type === "video" ? (
                          <video src={item.url} className="w-100 h-100 object-fit-cover" controls />
                        ) : (
                          <div className="d-flex flex-column justify-content-center align-items-center h-100 text-danger">
                            <FaFilePdf size={40} />
                            <p className="small mt-2">PDF</p>
                          </div>
                        )}
                      </div>
                      <p className="small mt-1 text-center text-truncate">{item.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="d-flex justify-content-between mt-4">
              {prevStep && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={prevStep}
                >
                  Previous
                </button>
              )}
              <div>
                <button
                  type="button"
                  className="btn btn-outline-primary me-2"
                  onClick={uploadFiles}
                  disabled={uploading || mediaFiles.length === 0}
                >
                  {uploading ? "Uploading..." : "Upload"}
                </button>
                <button type="submit" className="btn btn-primary">
                  Next
                </button>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Step3Medias;